import React from 'react';
import { ShoppingBag, Search, RefreshCw, Download, AlertTriangle, Shield, DollarSign } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { LoadingOverlay } from '../../components/ui/LoadingOverlay';
import { useLoading } from '../../hooks/useLoading';
import { sleep } from '../../utils/sleep';

const MarketplaceMonitoring = () => {
  const { isLoading, withLoading } = useLoading();

  const handleRefresh = async () => {
    await withLoading(async () => {
      await sleep(3000);
      console.log('Refreshing marketplace data...');
    });
  };

  return (
    <div className="space-y-6 relative">
      {isLoading && <LoadingOverlay message="Refreshing data..." />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <ShoppingBag className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">Marketplace Monitoring</h1>
            <p className="text-sm text-gray-400">Monitor and protect your brand across online marketplaces</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={handleRefresh}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Marketplace Stats */}
      <div className="grid grid-cols-4 gap-6">
        {[
          { label: 'Monitored Listings', value: '2,456', trend: '+89', icon: ShoppingBag },
          { label: 'Counterfeit Items', value: '34', trend: '-5', icon: AlertTriangle },
          { label: 'Protected Products', value: '156', trend: '+12', icon: Shield },
          { label: 'Revenue Impact', value: '$45K', trend: '-2.3K', icon: DollarSign }
        ].map((stat) => (
          <div key={stat.label} className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">{stat.label}</span>
              <stat.icon className="h-5 w-5 text-blue-500" />
            </div>
            <div className="flex items-end justify-between">
              <div className="text-2xl font-bold text-white">{stat.value}</div>
              <div className={`text-xs ${stat.trend.startsWith('+') ? 'text-emerald-500' : 'text-red-500'}`}>
                {stat.trend}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Marketplace Activity */}
      <div className="grid grid-cols-2 gap-6">
        {/* Active Violations */}
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <h2 className="text-lg font-semibold text-white mb-4">Active Violations</h2>
          <div className="space-y-4">
            {[
              { marketplace: 'Amazon', violations: 12, type: 'counterfeit', impact: '$12K' },
              { marketplace: 'eBay', violations: 8, type: 'unauthorized', impact: '$8K' },
              { marketplace: 'Alibaba', violations: 15, type: 'counterfeit', impact: '$15K' },
              { marketplace: 'Walmart', violations: 5, type: 'unauthorized', impact: '$5K' }
            ].map((violation) => (
              <div key={violation.marketplace} className="flex items-center justify-between p-4 bg-gray-800/30 rounded-lg">
                <div>
                  <div className="text-sm font-medium text-white">{violation.marketplace}</div>
                  <div className="text-xs text-gray-400 mt-1">{violation.violations} active violations</div>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="text-right">
                    <div className="text-sm font-medium text-red-500">{violation.impact}</div>
                    <div className="text-xs text-gray-400">impact</div>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    violation.type === 'counterfeit' ? 'bg-red-500/10 text-red-500' :
                    'bg-yellow-500/10 text-yellow-500'
                  }`}>
                    {violation.type}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Detections */}
        <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
          <h2 className="text-lg font-semibold text-white mb-4">Recent Detections</h2>
          <div className="space-y-4">
            {[
              { marketplace: 'Amazon', item: 'Premium Headphones', price: '$199', status: 'counterfeit', time: '10m ago' },
              { marketplace: 'eBay', item: 'Smart Watch Pro', price: '$299', status: 'unauthorized', time: '30m ago' },
              { marketplace: 'Alibaba', item: 'Wireless Earbuds', price: '$79', status: 'counterfeit', time: '1h ago' }
            ].map((detection, index) => (
              <div key={index} className="p-4 bg-gray-800/30 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">{detection.marketplace}</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    detection.status === 'counterfeit' ? 'bg-red-500/10 text-red-500' :
                    'bg-yellow-500/10 text-yellow-500'
                  }`}>
                    {detection.status}
                  </span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-white">{detection.item}</span>
                  <span className="text-sm font-medium text-white">{detection.price}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>{detection.time}</span>
                  {/* <Button variant="outline" size="sm">Take Action</Button> */}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Marketplace Coverage */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <h2 className="text-lg font-semibold text-white mb-4">Marketplace Coverage</h2>
        <div className="grid grid-cols-3 gap-6">
          {[
            { marketplace: 'Amazon', listings: 856, violations: 12, coverage: 95 },
            { marketplace: 'eBay', listings: 342, violations: 8, coverage: 85 },
            { marketplace: 'Alibaba', listings: 567, violations: 15, coverage: 75 }
          ].map((marketplace) => (
            <div key={marketplace.marketplace} className="p-4 bg-gray-800/30 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-white">{marketplace.marketplace}</span>
                <span className="text-xs text-gray-400">{marketplace.listings} listings</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">Coverage</span>
                  <span className="text-white">{marketplace.coverage}%</span>
                </div>
                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${marketplace.coverage}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">Active Violations</span>
                  <span className="text-red-500">{marketplace.violations}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MarketplaceMonitoring;